import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';

import { Button } from '@/shared/ui';

import type { TemplateChangeSummary } from '../lib';

import styles from './DroppedTemplateChanges.module.scss';

// A field list names its sections fields.
type Noun = 'section' | 'field';

const formatChange = (
  formatMessage: IntlShape['formatMessage'],
  change: TemplateChangeSummary,
  noun: Noun,
) => {
  switch (change.kind) {
    case 'renameSection':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.renameSection',
          defaultMessage:
            '{noun, select, field {Renamed the field “{from}” → “{to}”} other {Renamed the section “{from}” → “{to}”}}',
        },
        { noun, from: change.from, to: change.to },
      );
    case 'changeInstructions':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.changeInstructions',
          defaultMessage:
            '{noun, select, field {{removed, select, yes {Removed the AI instruction of the field “{section}”} other {Changed the AI instruction of the field “{section}” to “{to}”}}} other {{removed, select, yes {Removed the AI guidance of the section “{section}”} other {Changed the AI guidance of the section “{section}” to “{to}”}}}}',
        },
        {
          noun,
          section: change.section,
          to: change.to,
          removed: change.to ? 'no' : 'yes',
        },
      );
    case 'setRequired':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.setRequired',
          defaultMessage:
            '{noun, select, field {{required, select, yes {Made the field “{section}” required} other {Made the field “{section}” optional}}} other {{required, select, yes {Made the section “{section}” required} other {Made the section “{section}” optional}}}}',
        },
        {
          noun,
          section: change.section,
          required: change.required ? 'yes' : 'no',
        },
      );
    case 'setRewrite':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.setRewrite',
          defaultMessage:
            '{allowed, select, yes {Let AI rewrite the wording in “{section}”} other {Kept AI from rewriting the wording in “{section}”}}',
        },
        { section: change.section, allowed: change.allowed ? 'yes' : 'no' },
      );
    case 'addSection':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.addSection',
          defaultMessage:
            '{noun, select, field {Added the field “{section}”} other {Added the section “{section}”}}',
        },
        { noun, section: change.section },
      );
    case 'removeSection':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.removeSection',
          defaultMessage:
            '{noun, select, field {Deleted the field “{section}”} other {Deleted the section “{section}”}}',
        },
        { noun, section: change.section },
      );
    case 'moveSection':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.moveSection',
          defaultMessage:
            '{noun, select, field {Moved the field “{section}”} other {Moved the section “{section}”}}',
        },
        { noun, section: change.section },
      );
    case 'addLine':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.addLine',
          defaultMessage: 'Added the line “{line}” to “{section}”',
        },
        { line: change.line, section: change.section },
      );
    case 'removeLine':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.removeLine',
          defaultMessage: 'Removed the line “{line}” from “{section}”',
        },
        { line: change.line, section: change.section },
      );
    case 'moveLine':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.moveLine',
          defaultMessage: 'Moved the line “{line}” in “{section}”',
        },
        { line: change.line, section: change.section },
      );
    case 'changeLinks':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.changeLinks',
          defaultMessage:
            'Changed when the line “{line}” in “{section}” is shown',
        },
        { line: change.line, section: change.section },
      );
    case 'addBlock':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.addBlock',
          defaultMessage: 'Added a block to the line “{line}” in “{section}”',
        },
        { line: change.line, section: change.section },
      );
    case 'changeBlock':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.changeBlock',
          defaultMessage: 'Changed a block of the line “{line}” in “{section}”',
        },
        { line: change.line, section: change.section },
      );
    case 'removeBlock':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.removeBlock',
          defaultMessage:
            'Removed a block from the line “{line}” in “{section}”',
        },
        { line: change.line, section: change.section },
      );
    case 'changeWording':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.changeWording',
          defaultMessage:
            '{change, select, added {Added the wording “{to}” in “{section}”} removed {Removed the wording “{from}” in “{section}”} other {Changed the wording “{from}” → “{to}” in “{section}”}}',
        },
        {
          change: !change.from ? 'added' : !change.to ? 'removed' : 'changed',
          from: change.from,
          to: change.to,
          section: change.section,
        },
      );
    case 'changeHint':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.changeHint',
          defaultMessage:
            '{change, select, added {Added the hint on the empty block “{to}” in the line “{line}” in “{section}”} removed {Removed the hint on the empty block “{from}” in the line “{line}” in “{section}”} other {Changed the hint on the empty block “{from}” → “{to}” in the line “{line}” in “{section}”}}',
        },
        {
          change: !change.from ? 'added' : !change.to ? 'removed' : 'changed',
          from: change.from,
          to: change.to,
          line: change.line,
          section: change.section,
        },
      );
    case 'addOption':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.addOption',
          defaultMessage: 'Added the option “{option}” in “{section}”',
        },
        { option: change.option, section: change.section },
      );
    case 'renameOption':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.renameOption',
          defaultMessage: 'Renamed the option “{from}” → “{to}” in “{section}”',
        },
        { from: change.from, to: change.to, section: change.section },
      );
    case 'removeOption':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.removeOption',
          defaultMessage: 'Removed the option “{option}” in “{section}”',
        },
        { option: change.option, section: change.section },
      );
    case 'moveOption':
      return formatMessage(
        {
          id: 'workspace.templateEdit.dropped.moveOption',
          defaultMessage: 'Moved the option “{option}” in “{section}”',
        },
        { option: change.option, section: change.section },
      );
  }
};

type Props = {
  changes: TemplateChangeSummary[];
  noun: Noun;
  // A version was saved since the changes were dropped.
  saved?: boolean;
  className?: string;
  onHide: () => void;
};

// The changes a reload of the template's current version did not carry
// over, one line each, kept in sight until hidden so the clinician can make
// them again by hand.
export const DroppedTemplateChanges: FC<Props> = ({
  changes,
  noun,
  saved = false,
  className,
  onHide,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div
      role="status"
      className={cn('p4', styles.dropped, className)}
      data-component="workspace-template-dropped-changes"
    >
      <p className={styles.title}>
        {saved ? (
          <FormattedMessage
            id="workspace.templateEdit.droppedTitleSaved"
            defaultMessage="Changes from before the reload — check them against the saved version:"
          />
        ) : (
          <FormattedMessage
            id="workspace.templateEdit.droppedTitle"
            defaultMessage="The current version is loaded. Your changes were not carried over; make them again by hand:"
          />
        )}
      </p>
      <ul className={styles.list}>
        {changes.map((change, index) => (
          <li key={index}>{formatChange(formatMessage, change, noun)}</li>
        ))}
      </ul>
      <Button
        variant="tertiary"
        size="small"
        className={styles.hide}
        data-component="workspace-template-dropped-hide"
        onClick={onHide}
      >
        <FormattedMessage
          id="workspace.templateEdit.droppedHide"
          defaultMessage="Hide the list"
        />
      </Button>
    </div>
  );
};
